import type { Brand } from "utility-types";

export type DrawableAssetId = Brand<string, "DrawableAssetId">;

export interface AssetManager {
	set(assetId: DrawableAssetId, image: HTMLImageElement): Promise<void>;
}

export class DrawableAssets {
	private static imageRequestUrl(assetId: DrawableAssetId): string {
		// Cloudflare can cache an R2 response requested by an ordinary <img> without
		// the CORS header, then serve that response to WebGL. Keep GPU-bound image
		// requests on their own cache key, initially populated with Origin present.
		if (!assetId.startsWith("https://media.movesofconsequence.com/")) {
			return assetId;
		}
		const url = new URL(assetId);
		url.searchParams.set("webgl-cors", "1");
		return url.href;
	}

	public static externalAssetManagers: AssetManager[] = [];

	public static registerExternalAssetManager(manager: AssetManager): void {
		if (DrawableAssets.externalAssetManagers.includes(manager)) {
			return;
		}
		DrawableAssets.externalAssetManagers.push(manager);
	}

	public static readonly cache = new Map<DrawableAssetId, HTMLImageElement>();
	private static readonly loading = new Map<string, Promise<HTMLImageElement>>();
	private static isReadyImage(image: HTMLImageElement | undefined): image is HTMLImageElement {
		return Boolean(image?.complete && image.naturalWidth > 0 && image.naturalHeight > 0);
	}

	public static has(assetId: DrawableAssetId | DrawableAssetId[]): boolean {
		if (Array.isArray(assetId)) {
			return assetId.every((id) => DrawableAssets.has(id));
		}
		return DrawableAssets.isReadyImage(DrawableAssets.cache.get(assetId));
	}

	public static get(assetId: DrawableAssetId): HTMLImageElement | undefined {
		const image = DrawableAssets.cache.get(assetId);
		return DrawableAssets.isReadyImage(image) ? image : undefined;
	}

	public static list(): DrawableAssetId[] {
		return Array.from(DrawableAssets.cache.keys()) as DrawableAssetId[];
	}

	public static async load(
		assetIds: DrawableAssetId | DrawableAssetId[],
	): Promise<DrawableAssetId[]> {
		const ids = Array.isArray(assetIds) ? assetIds : [assetIds];
		await Promise.allSettled(ids.map((assetId) => DrawableAssets.loadOne(assetId)));
		return ids;
	}

	private static loadOne(assetId: DrawableAssetId): Promise<HTMLImageElement> {
		const inFlight = DrawableAssets.loading.get(assetId);
		if (inFlight) {
			return inFlight;
		}
		const cached = DrawableAssets.cache.get(assetId);
		if (DrawableAssets.isReadyImage(cached)) {
			return Promise.resolve(cached);
		}

		if (cached) {
			DrawableAssets.cache.delete(assetId);
		}

		const image = new Image();
		// Pixi uploads these images to WebGL. Cross-origin artwork must be loaded
		// in CORS mode before src is assigned, otherwise the texture is tainted.
		image.crossOrigin = "anonymous";
		const promise = new Promise<HTMLImageElement>((resolve, reject) => {
			image.onload = () => {
				if (!DrawableAssets.isReadyImage(image)) {
					DrawableAssets.cache.delete(assetId);
					reject(new Error(`Loaded image has invalid dimensions: ${assetId}`));
					return;
				}
				(async () => {
					for (const manager of DrawableAssets.externalAssetManagers) {
						console.log(`Loading asset ${assetId} with manager ${manager.constructor.name}`);
						await manager.set(assetId, image);
					}
				})()
					.then(() => {
						DrawableAssets.cache.set(assetId, image);
						resolve(image);
					})
					.catch((error: unknown) => {
						DrawableAssets.cache.delete(assetId);
						reject(error);
					});
			};
			image.onerror = () => {
				DrawableAssets.cache.delete(assetId);
				reject(new Error(`Failed to load image: ${assetId}`));
			};
		});

		DrawableAssets.loading.set(assetId, promise);
		image.src = DrawableAssets.imageRequestUrl(assetId);
		console.log(`Loading asset ${assetId} with src ${image.src}`);

		const clearLoading = () => DrawableAssets.loading.delete(assetId);
		void promise.then(clearLoading, clearLoading);

		return promise;
	}

	static isLoading(): boolean {
		return DrawableAssets.loading.size > 0;
	}
}
